/**
 * Standard component types (`config/buildComponentTypes`) a Kontrollmall can suggest
 * as `componentSpecificType`. Per-instance types (`{baseType}_custom_{key}`) are left
 * out: a template is shared configuration and should name types every building has.
 */

import { BaseType, BuildComponentTypeDefinition } from '../types/Enums';
import { translate } from '../types/EnumTranslations';
import { getBaseTypeOrder } from './baseTypeUtils';
import { groupBy } from './groupBy';

export interface ComponentTypeOption {
    key: string;
    name: string;
}

export interface ComponentTypeOptionGroup {
    baseType: BaseType;
    label: string;
    types: ComponentTypeOption[];
}

type TypeConfig = Record<string, BuildComponentTypeDefinition>;

/** A key of the shared type config, not a per-instance `_custom_` type */
export const isStandardComponentType = (key: string): boolean => key !== '' && !key.includes('_custom_');

/** The standard types with a Swedish name, grouped by base type in the app's base type order */
export const standardComponentTypeGroups = (types: TypeConfig): ComponentTypeOptionGroup[] => {
    const options = Object.entries(types)
        .filter(([key, definition]) => isStandardComponentType(key) && definition?.baseType && definition.translations?.sv)
        .map(([key, definition]) => ({ baseType: definition.baseType, key, name: definition.translations.sv }));
    return Array.from(groupBy(options, option => option.baseType).entries())
        .map(([baseType, group]) => ({
            baseType,
            label: translate(baseType),
            types: group.map(({ key, name }): ComponentTypeOption => ({ key, name })).sort((a, b) => a.name.localeCompare(b.name, 'sv'))
        }))
        .sort((a, b) => getBaseTypeOrder(a.baseType) - getBaseTypeOrder(b.baseType) || a.label.localeCompare(b.label, 'sv'));
};

/**
 * A CSV cell naming a standard type: its key, or a Swedish name that matches exactly one
 * standard type (case-insensitive). Empty → null; unknown or custom → undefined. Without
 * a loaded type config any key-shaped standard value is accepted.
 */
export const parseComponentTypeCell = (cell: string, types: TypeConfig): string | null | undefined => {
    const value = cell.trim();
    if (!value) return null;
    if (Object.keys(types).length === 0) {
        return isStandardComponentType(value) && /^[A-Za-z0-9_-]+$/.test(value) ? value : undefined;
    }
    if (types[value]) return isStandardComponentType(value) ? value : undefined;
    const lower = value.toLowerCase();
    const matches = Object.entries(types)
        .filter(([key, definition]) => isStandardComponentType(key) && definition?.translations?.sv?.toLowerCase() === lower)
        .map(([key]) => key);
    return matches.length === 1 ? matches[0] : undefined;
};
